import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  Autocomplete,
} from '@mui/material';
import {
  ReceiptLongOutlined,
  FilterAltOutlined,
  RefreshOutlined,
  CallMadeOutlined,
  CallReceivedOutlined,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import { Order, OrderStatus, OrderDirection, Partner } from '../../types/api';

const statusColors: Record<OrderStatus, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  [OrderStatus.CREATED]: 'info',
  [OrderStatus.PENDING]: 'default',
  [OrderStatus.PARTIAL_FULFILLED]: 'warning',
  [OrderStatus.COMPLETED]: 'success',
  [OrderStatus.CANCELLED]: 'error',
};

const statusLabels: Record<OrderStatus, string> = {
  [OrderStatus.CREATED]: 'Draft',
  [OrderStatus.PENDING]: 'Pending',
  [OrderStatus.PARTIAL_FULFILLED]: 'Partial Fulfilled',
  [OrderStatus.COMPLETED]: 'Completed',
  [OrderStatus.CANCELLED]: 'Cancelled',
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTodayString = () => {
  const today = new Date();
  return today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
};

const CustomerOrders: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial values from URL query params
  const initialStatus = searchParams.get('status') || '';
  const initialDirection = searchParams.get('direction') || '';
  const initialStartDate = searchParams.get('start_date') || getTodayString();
  const initialEndDate = searchParams.get('end_date') || getTodayString();

  // Filter states
  const [selectedCustomer, setSelectedCustomer] = useState<Partner | null>(null);
  const [status, setStatus] = useState<string>(initialStatus);
  const [direction, setDirection] = useState<string>(initialDirection);
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);

  // Data states
  const [customers, setCustomers] = useState<Partner[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);

  // Pagination states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 15;

  // Fetch customers on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await api.get('/api/partners', { params: { type: 'CUSTOMER' } });
        setCustomers(response.data.partners || []);
      } catch (err) {
        console.error('Error fetching customers:', err);
      }
    };
    fetchCustomers();
  }, []);

  // Fetch orders when filters or page changes
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit,
        partner_type: 'CUSTOMER',
      };

      if (selectedCustomer) {
        params.partner_id = selectedCustomer.id;
      }
      if (status) {
        params.status = status;
      }
      if (direction) {
        params.direction = direction;
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      const response = await api.get('/api/orders', { params });
      setOrders(response.data.data || response.data.orders || []);
      setTotal(response.data.meta?.total || 0);
      const total = response.data.meta?.total || 0;
      setTotalPages(Math.ceil(total / limit) || 1);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    // Sync filters to URL
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (direction) params.direction = direction;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (selectedCustomer) params.partner_id = String(selectedCustomer.id);
    setSearchParams(params, { replace: true });
    fetchOrders();
  };

  const handleReset = () => {
    setSelectedCustomer(null);
    setStatus('');
    setDirection('');
    setStartDate(getTodayString());
    setEndDate(getTodayString());
    setPage(1);
    setSearchParams({}, { replace: true });
    setTimeout(fetchOrders, 0);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ReceiptLongOutlined sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
        <Typography variant="h6" fontWeight="bold">
          Customer Orders
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <FilterAltOutlined color="action" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={500}>
            Filters
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' },
            gap: 1.5,
            mb: 1.5,
          }}
        >
          {/* Customer Filter */}
          <Autocomplete
            options={customers}
            getOptionLabel={(option) => option.name}
            value={selectedCustomer}
            onChange={(_, newValue) => setSelectedCustomer(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Customer" placeholder="All Customers" size="small" />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {/* Direction Filter */}
          <FormControl size="small" fullWidth>
            <InputLabel>Direction</InputLabel>
            <Select
              value={direction}
              label="Direction"
              onChange={(e) => setDirection(e.target.value)}
            >
              <MenuItem value="">All Directions</MenuItem>
              <MenuItem value="OUTWARD">Outward</MenuItem>
              <MenuItem value="RETURN">Return / Inward</MenuItem>
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value={OrderStatus.CREATED}>Draft</MenuItem>
              <MenuItem value={OrderStatus.PENDING}>Pending</MenuItem>
              <MenuItem value={OrderStatus.PARTIAL_FULFILLED}>Partial Fulfilled</MenuItem>
              <MenuItem value={OrderStatus.COMPLETED}>Completed</MenuItem>
              <MenuItem value={OrderStatus.CANCELLED}>Cancelled</MenuItem>
            </Select>
          </FormControl>

          {/* Start Date */}
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {/* End Date */}
          <TextField
            label="To Date"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="contained" size="small" onClick={handleSearch} disabled={loading}>
            Search
          </Button>
          <Button variant="outlined" size="small" onClick={handleReset} startIcon={<RefreshOutlined />}>
            Reset
          </Button>
        </Box>
      </Paper>

      {/* Orders Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2">Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">No orders found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography 
                        variant="body2"
                        fontWeight={500} 
                        onClick={() => navigate(`/customers/orders/${order.id}`)}
                        sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                      >
                        #{order.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {order.direction === OrderDirection.OUTWARD ? (
                          <CallMadeOutlined sx={{ fontSize: 16 }} color="primary" />
                        ) : (
                          <CallReceivedOutlined sx={{ fontSize: 16 }} color="secondary" />
                        )}
                        <Typography variant="body2">
                          {order.direction === OrderDirection.OUTWARD ? 'Outward' : 'Inward'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{order.partner_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[order.status]}
                        color={statusColors[order.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(order.created_at)}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {orders.length} of {total} orders
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CustomerOrders;
